// deps
import { useEffect, useState, useCallback, useContext } from "react";

// hooks
import { useMountedRef, captureError } from "@swiggy-private/core-ui";

// helpers
import { ProcessOrderReturnType } from "@payment-client/helpers/OrderHelper";
import { getTransactionConfirmationRedirectUrl, processTransaction } from "@payment-client/helpers/TransactionHelper";
import { saveProcessTransactionState } from "@payment-client/helpers/CheckoutStateHelper";
import { shouldPollForPennyTxnStatus } from "@payment-client/helpers/PollingHelpers";

// contexts
import { CheckoutContextValue, ApiContext, CheckoutActionTypes, ConfigContext } from "@swiggy-private/checkout-core";

// utils
import { hideNativeHeader, showNativeAlert, showNativeHeader } from "@payment-client/utils/Native";

// constants
import { PAYMENT_BACK_ALERT_OPTIONS } from "@payment-client/constants/TextDict";

const useProcessTransaction = (checkoutContext: CheckoutContextValue): [boolean] => {
    const isMountedRef = useMountedRef();
    const configValue = useContext(ConfigContext);
    const apiContextValue = useContext(ApiContext);
    const [processingTransaction, setProcessingTransaction] = useState(false);

    const { checkoutState, setCheckoutState } = checkoutContext;
    const apiHost = apiContextValue.host.unwrap();

    const reset = useCallback((): void => {
        // setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
        setProcessingTransaction(false);
    }, []);

    useEffect(() => {
        if (
            isMountedRef.current &&
            !processingTransaction &&
            checkoutState.type === CheckoutActionTypes.PROCESS_TRANSACTION
        ) {
            setProcessingTransaction(true);
        }
    }, [checkoutState.type, isMountedRef, processingTransaction]);

    useEffect(() => {
        // is mounted check..
        if (
            !isMountedRef.current ||
            !processingTransaction ||
            checkoutState.type !== CheckoutActionTypes.PROCESS_TRANSACTION
        ) {
            return;
        }

        const { transaction, transactionReqData, useJuspaySDK } = checkoutState.payload;

        processTransaction(checkoutState.payload)
            .then(async (processOrderData: ProcessOrderReturnType | void) => {
                if (!isMountedRef.current) {
                    return;
                }

                if (!processOrderData || !processOrderData.orderId) {
                    reset();
                    return;
                }

                const {
                    internalRedirect = false,
                    shouldRedirect = false,
                    redirectURI,
                    orderId,
                    redirectFn,
                } = processOrderData;

                const isRedirect = internalRedirect || (shouldRedirect && redirectURI);

                if (isRedirect) {
                    // save checkout state context & data in sessionStorage before naviagtion
                    saveProcessTransactionState(orderId, checkoutState);
                    setCheckoutState({
                        type: CheckoutActionTypes.SHOW_FULL_PAGE_LOADER,
                    });

                    showNativeHeader({
                        title: "MAKE PAYMENT",
                        backURL: getTransactionConfirmationRedirectUrl(),
                        backAlert: PAYMENT_BACK_ALERT_OPTIONS,
                    });

                    if (shouldRedirect && redirectURI) {
                        window.location.href = redirectURI;
                    }

                    // call the redirect function if available
                    if (internalRedirect && redirectFn) {
                        redirectFn();
                    }

                    return;
                }

                if (isMountedRef.current) {
                    setCheckoutState({
                        type: shouldPollForPennyTxnStatus(transaction)
                            ? CheckoutActionTypes.PENNY_TXN_STATUS_POLLER
                            : CheckoutActionTypes.CONFIRM_TRANSACTION,
                        payload: {
                            orderId,
                            transaction,
                            transactionReqData,
                            useJuspaySDK,
                        },
                    });
                }

                setProcessingTransaction(false);
            })
            .catch((err) => {
                captureError(err);
                if (isMountedRef.current) {
                    hideNativeHeader();
                    showNativeAlert();
                    reset();
                }
            });
    }, [
        processingTransaction,
        checkoutState,
        isMountedRef,
        setCheckoutState,
        reset,
        apiHost,
        configValue.paymentReturnUrl,
    ]);

    return [processingTransaction];
};

export default useProcessTransaction;
